import { useRef, useState } from "react";
import { Bookmark, Check, RefreshCw, Save, Trash2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/PageHeader";
import { ApiError } from "@/api/client";
import { createKeptWork, deleteKeptWork, getKeptWork, listKeptWork, updateKeptWork,
  type KeptSnapshot, type KeptWork, type KeptWorkSummary } from "@/api/composer";
import { ContinueKeptWorkPanel } from "./ContinueKeptWorkPanel";

/** Diagnostic CRUD controls, not the approved Android interaction design. */
export function KeptWorkPanel({ initial }: { initial: KeptSnapshot }) {
  const [items, setItems] = useState<KeptWorkSummary[]>([]);
  const [selected, setSelected] = useState<KeptWork | null>(null);
  const [editor, setEditor] = useState(JSON.stringify(initial, null, 2));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const saveKey = useRef(crypto.randomUUID());
  async function refreshList() { setItems((await listKeptWork()).data.items); }
  function show(item: KeptWork) { setSelected(item); setEditor(JSON.stringify(item.snapshot, null, 2)); }
  async function run(action: () => Promise<void>) {
    setBusy(true); setMessage("");
    try { await action(); }
    catch (error) { setMessage(error instanceof ApiError ? `${error.code}: ${error.message}`
      : error instanceof Error ? error.message : "Request failed"); }
    finally { setBusy(false); }
  }
  const iconStyle = "mr-1 h-4 w-4";
  return <section className="mt-5 space-y-3 rounded-xl border bg-white p-4" aria-label="Private kept work">
    <h3 className="flex items-center font-semibold"><Bookmark className={iconStyle} />Private kept work</h3>
    <p className="text-sm">Up to 50 items, retained until you delete them or your account. No automatic searches, notifications or publication.</p>
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(refreshList)}><RefreshCw className={iconStyle} />Load saved work</Button>
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => {
        if (window.confirm("Discard this editor draft and start a new save?")) {
          setSelected(null); setEditor(JSON.stringify(initial, null, 2)); saveKey.current = crypto.randomUUID(); setMessage("");
        }
      }}>New from current request</Button>
    </div>
    <ul className="space-y-1">{items.map(item => <li key={item.id}>
      <button className="text-left text-sm underline" disabled={busy} onClick={() => run(async () => {
        if (window.confirm("Replace the editor with this saved work? Unsaved changes will be discarded.")) show((await getKeptWork(item.id)).data);
      })}>{item.title} · {item.status} · v{item.version}</button>
    </li>)}</ul>
    <label className="block text-sm">Member snapshot (JSON; sources accept type and ID only)
      <textarea className="mt-1 block min-h-64 w-full rounded border p-2 font-mono text-xs" value={editor}
        disabled={busy} onChange={event => setEditor(event.target.value)} />
    </label>
    {selected && <p className="text-xs">Editing {selected.id} · version {selected.version}. Reopen means active; it does not publish or refresh the search.</p>}
    <div className="flex flex-wrap gap-2">
      <Button size="sm" disabled={busy} onClick={() => run(async () => {
        const snapshot = JSON.parse(editor) as KeptSnapshot;
        const saved = selected ? await updateKeptWork(selected.id, selected.version, { snapshot })
          : await createKeptWork(saveKey.current, snapshot);
        show(saved.data); await refreshList(); setMessage("Saved privately.");
      })}><Save className={iconStyle} />{selected ? "Save changes" : "Keep privately"}</Button>
      {selected && <>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(async () => {
          show((await updateKeptWork(selected.id, selected.version, { status: selected.status === "active" ? "finished" : "active" })).data);
          await refreshList();
        })}>{selected.status === "active" ? <Check className={iconStyle} /> : <Undo2 className={iconStyle} />}{selected.status === "active" ? "Finish" : "Reopen"}</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(async () => {
          if (window.confirm("Reload the server version? The current editor text will be discarded.")) show((await getKeptWork(selected.id)).data);
        })}><RefreshCw className={iconStyle} />Reload version</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(async () => {
          if (!window.confirm("Delete this private saved work? Published contributions are not deleted.")) return;
          await deleteKeptWork(selected.id, selected.version);
          setSelected(null); setEditor(JSON.stringify(initial, null, 2)); saveKey.current = crypto.randomUUID();
          await refreshList(); setMessage("Deleted private saved work.");
        })}><Trash2 className={iconStyle} />Delete</Button>
      </>}
    </div>
    {message && <p role="status" className="text-sm">{message}</p>}
    {selected && <ContinueKeptWorkPanel key={`${selected.id}:${selected.version}`} item={selected}
      disabled={busy || editor !== JSON.stringify(selected.snapshot, null, 2)}
      onUseRequirements={context => {
        if (context.basedOn?.id !== selected.id || context.basedOn.version !== selected.version) return;
        if (!window.confirm("Use these working requirements in the editor? Nothing will be saved yet.")) return;
        setEditor(JSON.stringify({ ...JSON.parse(editor) as KeptSnapshot,
          constraints: context.constraints, unresolvedQuestions: context.unresolvedQuestions }, null, 2));
      }} />}
    {selected && <div className="space-y-1 text-sm">
      <p>Source access: {selected.sourceRefresh.status}. A failed check is not proof that sources were removed.</p>
      {selected.sourceRefresh.sources.map(source => <p key={`${source.reference.type}:${source.reference.id}`}>{source.title} — {source.destinationPath}</p>)}
      {selected.sourceRefresh.unavailableReferences.length > 0 && <p>{selected.sourceRefresh.unavailableReferences.length} sources are unavailable. No saved copy is shown.</p>}
    </div>}
  </section>;
}
